import React, { forwardRef, useEffect, useState } from 'react';
import { MenuItem, Select } from '@mui/material';
import Stack from '@mui/material/Stack';
import { observer } from 'mobx-react';
import { DATE_FORMAT } from '@ui-framework/index';
import Box from '@mui/material/Box';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import DateRangePicker from './DateRangePicker';

export const yupDateRangeValidation = ({
  testName,
  sameErrMessage,
  greaterThnErrMessage,
  validBetweenDateMessage,
  requiredMessage,
  validDateMessage,
  useDatetimeValidation = false,
}) =>
  yup
    .object()
    .nullable()
    // eslint-disable-next-line func-names
    .test(testName, function (value) {
      if (value?.operator === 'eqNull') {
        return true;
      }
      if (value === null || !value || value?.operator === 'none') {
        return true;
      }
      if (value?.operator === 'between') {
        if (value?.value) {
          if (value?.value?.indexOf(',') !== -1) {
            const dates = value?.value?.split(',');
            if (dayjs(dates?.[0]).isValid() && dayjs(dates?.[1]).isValid()) {
              const date1 = dayjs(dates?.[0]);
              const date2 = dayjs(dates?.[1]);

              if (useDatetimeValidation) {
                if (date1.isSame(date2, 'minute')) {
                  return this.createError({
                    message: sameErrMessage,
                  });
                }
                if (date1.isAfter(date2, 'minute')) {
                  return this.createError({
                    message: greaterThnErrMessage,
                  });
                }
                return true;
              }

              if (date1.isSame(date2, 'day')) {
                return this.createError({
                  message: sameErrMessage,
                });
              }
              if (date1.isAfter(date2, 'day')) {
                return this.createError({
                  message: greaterThnErrMessage,
                });
              }
              return true;
            }
          }
        }
        return this.createError({
          message: validBetweenDateMessage,
        });
      }
      if (!value?.value) {
        return this.createError({
          message: requiredMessage,
        });
      }
      if (!dayjs(value?.value).isValid()) {
        return this.createError({
          message: validDateMessage,
        });
      }
      return true;
    });

function parseDateRange(input, dateFormat) {
  if (!input || typeof input !== 'string') return [null, null];
  const parts = input.split(',').map(d => d.trim());
  if (parts.length !== 2) return [null, null];
  const [start, end] = parts;

  const validateDate = (s, fmt) => s && dayjs(s, fmt, true).isValid();

  if (
    start &&
    end &&
    validateDate(start, dateFormat) &&
    validateDate(end, dateFormat)
  ) {
    return [start, end];
  }
  if (start && validateDate(start, dateFormat)) {
    return [start, null];
  }
  if (end && validateDate(end, dateFormat)) {
    return [null, end];
  }
  return [null, null];
}

function dateArrayToFormattedDate(arrayDate, dateFormat) {
  if (Array.isArray(arrayDate)) {
    return arrayDate
      .map(d => (d ? dayjs(d).format(dateFormat) : ''))
      ?.join(',');
  }
  return null;
}

const CustomDatePicker = React.forwardRef((props, ref) => {
  const { enableTimeSelection, ...rest } = props;
  const FieldPicker = enableTimeSelection ? DateTimePicker : DatePicker;

  return (
    <FieldPicker
      {...rest}
      ref={ref}
      sx={{
        '& .MuiInputBase-input.MuiOutlinedInput-input': {
          padding: '6px 0px 6px 6px!important',
        },
      }}
      slotProps={{
        textField: {
          error: !!rest?.error,
          helperText: rest?.error?.message,
          size: 'small',
        },
        field: {
          clearable: true,
          onClear: () => {
            rest.onChange({ operator: rest.operator || 'none', value: null });
          },
        },
      }}
    />
  );
});

const DynamicDateRangePicker = forwardRef((props, ref) => {
  const { control, watch } = useFormContext();

  const enableTimeSelection = Boolean(
    props.field?.enableTimeSelection ?? props.enableTimeSelection,
  );

  const dateFormat = enableTimeSelection
    ? DATE_FORMAT.STANDARD_DATETIME
    : DATE_FORMAT.STANDARD_DATE;

  const watchedValue = watch(props?.name);

  const initialOp = (watchedValue && watchedValue?.operator) || 'none';

  const [selectedOperator, setSelectedOperator] = useState(initialOp);

  useEffect(() => {
    if (!watchedValue) {
      setSelectedOperator('none');
    }
  }, [watchedValue]);

  return (
    <Box ref={ref}>
      <Controller
        name={props?.name}
        control={control}
        render={({ field: controllerField }) => {
          const { value } = controllerField;

          const change = (operator, changeValue) => {
            if (operator === 'eqNull') {
              const newValueToSet = {
                operator,
                value: dayjs().format(dateFormat),
              };
              controllerField.onChange(newValueToSet);
            } else if (operator === 'none') {
              controllerField.onChange(null);
            } else {
              const newValueToSet = {
                operator,
                value: changeValue,
              };
              controllerField.onChange(newValueToSet);
            }
          };

          return (
            <>
              <Stack direction="row" gap={1}>
                <Box>
                  <Select
                    sx={{ marginTop: '-5px' }}
                    size="small"
                    value={selectedOperator}
                    onChange={e => {
                      setSelectedOperator(e.target.value);
                      change(e.target.value, null);
                    }}>
                    <MenuItem value="none">No Selection</MenuItem>
                    <MenuItem value="eqNull">None</MenuItem>
                    <MenuItem value="eq">Equal to</MenuItem>
                    <MenuItem value="lte">Less than equal to</MenuItem>
                    <MenuItem value="gte">Greater than equal to</MenuItem>
                    <MenuItem value="between">Between</MenuItem>
                  </Select>
                </Box>
                <Box>
                  {['eq', 'gte', 'lte'].includes(selectedOperator) && (
                    <CustomDatePicker
                      {...controllerField}
                      {...props}
                      enableTimeSelection={enableTimeSelection}
                      value={value?.value ? dayjs(value?.value) : null}
                      onChange={d => {
                        change(selectedOperator, dayjs(d).format(dateFormat));
                      }}
                      error={value?.value ? null : { message: 'Required' }}
                    />
                  )}
                </Box>
              </Stack>

              {selectedOperator === 'between' && (
                <Box sx={{ mt: 1 }}>
                  <DateRangePicker
                    enableTimeSelection={enableTimeSelection}
                    value={parseDateRange(value?.value, dateFormat)}
                    {...props}
                    onChange={e => {
                      change(
                        'between',
                        dateArrayToFormattedDate(e.target.value, dateFormat),
                      );
                    }}
                  />
                </Box>
              )}
            </>
          );
        }}
      />
    </Box>
  );
});

export default observer(DynamicDateRangePicker);
